import React, { useCallback, useEffect } from 'react';

import TopNavItem from '@components/Nav/TopNav/NavItem';

import { useSectionRegistryState } from './SectionRegistryContext';
import { MY_TI_NAV_ITEMS } from './constants';

const SCROLL_DELAY_MS = 100;

const MyTINav = () => {
  const registeredSections = useSectionRegistryState();

  const visibleNavItems = MY_TI_NAV_ITEMS.filter((item) => registeredSections.has(item.id));

  const scrollToSection = useCallback((sectionId: string) => {
    const target = document.getElementById(sectionId);
    if (!target) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const href = e.currentTarget.getAttribute('href');
      const sectionId = href?.substring(1);
      if (sectionId) {
        scrollToSection(sectionId);
        window.history.pushState(null, '', href);
      }
    },
    [scrollToSection]
  );

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash && registeredSections.has(hash)) {
      const timeout = setTimeout(() => scrollToSection(hash), SCROLL_DELAY_MS);
      return () => clearTimeout(timeout);
    }
  }, [registeredSections, scrollToSection]);

  if (visibleNavItems.length === 0) return null;

  return (
    <nav
      aria-label="Page sections"
      className="mt-48 border-0 border-b border-t border-solid border-grayscale-beige py-32 [&_+_article]:border-t-0 [&_+_article]:pt-0"
    >
      <ul className="m-0 flex flex-1 list-none flex-col items-center justify-center p-0 sm:flex-row sm:gap-24">
        {visibleNavItems.map((item, index) => (
          <TopNavItem
            key={item.id}
            as="link"
            className="before:hidden sm:before:block"
            href={`#${item.id}`}
            showLeftDivider={index > 0}
            onClick={handleClick}
          >
            {item.label}
          </TopNavItem>
        ))}
      </ul>
    </nav>
  );
};

export default MyTINav;
